import React from "react"
import JSZip from "jszip"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as sessions from "sessions"
import * as api from "brandguard/api"
import * as cache from "media/cache"
import * as media from "media"
import * as bglayouts from "brandguard/components/layouts"

interface props {
	uploadItems: api.DocumentSearchResponseItem[]
	historicalItems: api.DocumentSearchResponseItem[]
	deleteAction?(
		bid: string,
		id: string,
		req: api.DocumentPatchRequest,
		...options: httpx.option[]
	): Promise<api.DocumentPatchResponse>
	onDelete?(items: api.DocumentSearchResponseItem[]): void
}

export default function MultiActions(props: React.PropsWithChildren<props>): JSX.Element {
	const { uploadItems, historicalItems, deleteAction, onDelete, children } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const bearertoken = sessions.useToken()
	const allItems = [...uploadItems, ...historicalItems]

	const handleDelete = (): void => {
		const promises: Promise<api.DocumentPatchResponse>[] = []
		uploadItems.forEach((i) => {
			i.media!.tombstoned_at = new Date().toISOString()
			promises.push(deleteAction!(i.media!.brand_id, i.media!.id, i, authzc.bearer(authzaccount)))
		})
		Promise.all(promises)
			.then((results) => {
				const items: api.DocumentSearchResponseItem[] = results.map((r) => ({ ...r, pages: [] }))
				onDelete!(items)
			})
			.catch((cause) => console.error("unable to multi-select brandguard documents", cause))
	}

	const handleDownload = (): void => {
		if (uploadItems.length === 1) {
			downloadSingle(uploadItems[0])
			return
		}

		const zip = new JSZip()

		const downloadZip = (): void => {
			zip.generateAsync({ type: "blob" }).then((content) => {
				const downloadLink = document.createElement("a")
				downloadLink.href = URL.createObjectURL(content)
				downloadLink.download = "brandguard-documents.zip"
				downloadLink.click()
			})
		}

		const promises: Promise<api.DocumentPatchRequest | JSZip>[] = []
		allItems.forEach((i) => {
			promises.push(
				cache.current(authzaccount.current.id, i.media!.md5, bearertoken).then((url) => {
					return fetch(url)
						.then((response) => {
							return response.arrayBuffer()
						})
						.then((documentBuffer) => {
							const documentName = media.downloads.filename(i.media!.md5, i.media!.mimetype)
							return zip.file(documentName, documentBuffer)
						})
				}),
			)
		})

		Promise.all(promises).then(() => {
			downloadZip()
		})
	}

	const downloadSingle = (i: api.DocumentSearchResponseItem): void => {
		media.downloads.file(
			i.media!.md5,
			i.media!.mimetype,
			cache.current(authzaccount.current.id, i.media!.md5, bearertoken),
		)
	}

	if (!allItems.length) return <></>

	return (
		<bglayouts.DisplayActions
			cnt={allItems.length}
			canDelete={uploadItems.length > 0 && deleteAction !== undefined && onDelete !== undefined}
			handleDelete={handleDelete}
			handleDownload={handleDownload}
		>
			{children}
		</bglayouts.DisplayActions>
	)
}

MultiActions.defaultProps = {
	uploadItems: [],
	historicalItems: [],
}
